/**
 * Slug ↔ view bidirectional map for the React Router migration.
 *
 * Source of truth for the URL surface area. Dutch slugs by design — Stagery's
 * audience is Dutch makelaars, and the in-app sidebar already labels these in
 * Dutch ("Woningen", "Abonnement", "Profiel", "Beheerconsole"). International
 * expansion later can add a locale prefix (e.g. `/en/properties`).
 *
 * Keep this file in sync with `src/routes/index.tsx` route config.
 */

export type AppView =
  | 'overview'
  | 'properties'
  | 'startStaging'
  | 'dashboard'
  | 'studio'
  | 'variants'
  | 'batch'
  | 'team'
  | 'profile'
  | 'subscription'
  | 'referrals'
  | 'feedback';

/**
 * View → URL path. Wizard steps (`dashboard`, `variants`, `studio`, `batch`)
 * nest under `/eigendommen/:propertyId/...` so a property is required in the
 * URL — this fixes the prior state-bug where `studio` was reachable with no
 * property selected. When no property is in scope, callers should set view to
 * `properties` instead.
 *
 * STA-199: `startStaging` is the batch-upload entry point at `/upload` —
 * multiple photos + AI selection. `dashboard` is the single-photo Studio
 * step (reachable via lightbox "Nieuwe restyling" only), at `/single`.
 */
export const VIEW_TO_PATH: Record<AppView, (propertyId?: string) => string> = {
  overview: () => '/',
  properties: () => '/eigendommen',
  startStaging: (id) => (id ? `/eigendommen/${id}/upload` : '/eigendommen'),
  dashboard: (id) => (id ? `/eigendommen/${id}/single` : '/eigendommen'),
  variants: (id) => (id ? `/eigendommen/${id}/varianten` : '/eigendommen'),
  studio: (id) => (id ? `/eigendommen/${id}/studio` : '/eigendommen'),
  batch: (id) => (id ? `/eigendommen/${id}/batch` : '/eigendommen'),
  team: () => '/team',
  profile: () => '/profiel',
  subscription: () => '/abonnement',
  referrals: () => '/referrals',
  feedback: () => '/feedback',
};

/** Reverse map — pathname → view. Wizard sub-paths checked before `/eigendommen`. */
export function pathToView(pathname: string): AppView {
  if (pathname === '/' || pathname === '') return 'overview';
  if (pathname.startsWith('/eigendommen/')) {
    if (pathname.endsWith('/upload')) return 'startStaging';
    if (pathname.endsWith('/single')) return 'dashboard';
    if (pathname.endsWith('/varianten')) return 'variants';
    if (pathname.endsWith('/studio')) return 'studio';
    if (pathname.endsWith('/batch')) return 'batch';
    return 'properties';
  }
  if (pathname === '/eigendommen') return 'properties';
  if (pathname.startsWith('/team')) return 'team';
  if (pathname.startsWith('/profiel')) return 'profile';
  if (pathname.startsWith('/abonnement')) return 'subscription';
  if (pathname.startsWith('/referrals')) return 'referrals';
  if (pathname.startsWith('/feedback')) return 'feedback';
  return 'overview';
}

/** Extract `:propertyId` from `/eigendommen/:propertyId/...` paths. */
export function pathToPropertyId(pathname: string): string | null {
  const m = pathname.match(/^\/eigendommen\/([^/]+)/);
  return m ? m[1] : null;
}
